import { useState, useRef, useCallback, useEffect } from 'react';
import { cleanText } from '../utils/textHelpers';
import { Step } from '../types';
import { useSpeechVoice } from '../contexts/SpeechVoiceContext';

interface UseSpeechSynthesisProps {
  steps: Step[];
  onStepChange?: (stepIndex: number) => void;
}

interface UseSpeechSynthesisReturn {
  isSpeaking: boolean;
  speakStep: (index: number) => void;
  stopSpeech: () => void;
}

export const useSpeechSynthesis = ({
  steps,
  onStepChange
}: UseSpeechSynthesisProps): UseSpeechSynthesisReturn => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSessionRef = useRef(0);
  const { selectedVoice } = useSpeechVoice();

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const stopSpeech = useCallback(() => {
    speechSessionRef.current += 1;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const speakStep = useCallback((index: number) => {
    if (!steps[index]) return;

    const step = steps[index];

    const segments: string[] = [];
    if (step.title?.trim()) segments.push(`Step ${index + 1} of ${steps.length}. ${step.title}.`);
    if (step.why?.trim()) segments.push(`Why. ${step.why}.`);
    if (step.action?.trim()) segments.push(`Action. ${step.action}.`);
    if (step.result?.trim()) segments.push(`Result. ${step.result}.`);

    const math = (step.math || []).filter((m) => m && m.trim());
    if (math.length > 0) {
      segments.push(`Calculation. ${math.join('. ')}.`);
    }

    const spokenSegments = segments
      .map((s) => cleanText(s))
      .filter((s) => s && s.trim());

    if (spokenSegments.length === 0) return;

    speechSessionRef.current += 1;
    const sessionId = speechSessionRef.current;

    window.speechSynthesis.cancel();

    const speakSegment = (segmentIndex: number) => {
      if (speechSessionRef.current !== sessionId) return;
      if (!spokenSegments[segmentIndex]) return;

      const utterance = new SpeechSynthesisUtterance(spokenSegments[segmentIndex]);

      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang;
      } else {
        utterance.lang = 'en-US';
      }

      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 1;

      utterance.onstart = () => setIsSpeaking(true);

      utterance.onend = () => {
        if (speechSessionRef.current !== sessionId) return;

        const nextSegmentIndex = segmentIndex + 1;
        if (nextSegmentIndex < spokenSegments.length) {
          setTimeout(() => {
            speakSegment(nextSegmentIndex);
          }, 350);
          return;
        }

        setIsSpeaking(false);
        if (index + 1 < steps.length) {
          setTimeout(() => {
            if (speechSessionRef.current !== sessionId) return;
            onStepChange?.(index + 1);
            speakStep(index + 1);
          }, 700);
        }
      };

      window.speechSynthesis.speak(utterance);
    };

    speakSegment(0);
  }, [steps, onStepChange, selectedVoice]);

  return {
    isSpeaking,
    speakStep,
    stopSpeech,
  };
};
